import { AriaAttributes, AriaRole, MouseEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton, Stack } from '@mui/material';

import clsx from 'clsx';

import { DEFAULT_AVATAR_ID } from '../../constants';
import { i18nNS } from '../../i18n';
import { generateClasses } from '../../utils/helpers';
import CourseUserAvatar from '../CourseUserAvatar';
import { Props as UserAvatarProps } from '../UserAvatar';
import { Action, Content, Root, Subtitle, Title, userCellClasses } from '../UserCell/styles';
import { StyledDivider } from './styles';
import useCourseUserCell, { UseCourseUserCell } from './vm';

const courseUserCellClasses = {
  ...userCellClasses,
  ...generateClasses('CourseUserCell', ['subtitleDivider', 'subtitleText', 'removedMember']),
};

export { courseUserCellClasses };

export type CourseUserCellProps = DistributiveOmit<UseCourseUserCell, 'avatarId'> &
  AriaAttributes & {
    id?: string;
    className?: string;
    classes?: Partial<typeof courseUserCellClasses>;
    /** name is used for displaying initials, name is not directly displayed anywhere */
    name?: string;
    action?: ReactNode;
    edge?: 'start' | 'end';
    avatar?: string | UserAvatarProps | typeof DEFAULT_AVATAR_ID;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    /** @default 'medium' */
    size?: 'large' | 'medium';
    tabIndex?: number;
    role?: AriaRole;
    hideAriaContent?: boolean;
  };

const CourseUserCell = ({
  id,
  classes,
  className,
  onClick,
  avatar,
  name,
  action,
  size = 'medium',
  hideAriaContent,
  ...props
}: CourseUserCellProps) => {
  const { t } = useTranslation([i18nNS.COMMON]);

  const { cellTitle, cellSubtitle, cellAriaLabel } = useCourseUserCell({
    ...props,
    avatarId: typeof avatar === 'string' ? avatar : avatar?.avatarId ?? DEFAULT_AVATAR_ID,
  });

  const userAvatarProps = {
    variant: 'rounded' as const,
    className: clsx(courseUserCellClasses.avatar, classes?.avatar),
    avatarId: typeof avatar === 'string' ? avatar : avatar?.avatarId ?? '',
    bucketUrl: typeof avatar === 'string' ? undefined : avatar?.bucketUrl,
    name: name || (typeof cellTitle === 'string' ? cellTitle : undefined),
    'aria-hidden': hideAriaContent,
  };

  return (
    <Root
      id={id}
      aria-label={cellAriaLabel}
      {...props}
      className={clsx(className, courseUserCellClasses.root, classes?.root, {
        [courseUserCellClasses.sizeMedium]: size === 'medium',
        [courseUserCellClasses.sizeLarge]: size === 'large',
      })}
      onClick={onClick}
    >
      {avatar != null && (
        <>
          {props.isCourseRoleNeeded ? (
            <CourseUserAvatar
              {...userAvatarProps}
              isEmailIdNeeded={props.isEmailIdNeeded}
              isCourseRoleNeeded={props.isCourseRoleNeeded}
              existingCourseRole={props.existingCourseRole}
            />
          ) : (
            <CourseUserAvatar
              {...userAvatarProps}
              isEmailIdNeeded={props.isEmailIdNeeded}
              isCourseRoleNeeded={props.isCourseRoleNeeded}
            />
          )}
        </>
      )}
      <Content
        className={clsx(courseUserCellClasses.content, classes?.content)}
        aria-hidden={hideAriaContent}
      >
        {cellTitle ? (
          <>
            {typeof cellTitle === 'string' ? (
              <Title className={clsx(courseUserCellClasses.title, classes?.title)}>{cellTitle}</Title>
            ) : (
              cellTitle
            )}
          </>
        ) : (
          <Skeleton variant="text" width={120} />
        )}
        {cellTitle ? (
          cellSubtitle ? (
            <>
              {typeof cellSubtitle.subtitle === 'string' ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={2}
                  className={clsx(courseUserCellClasses.subtitle, classes?.subtitle)}
                  divider={
                    <StyledDivider
                      orientation="vertical"
                      flexItem
                      className={clsx(courseUserCellClasses.subtitleDivider, classes?.subtitleDivider)}
                    />
                  }
                >
                  <Subtitle
                    className={clsx(courseUserCellClasses.subtitleText, classes?.subtitleText)}
                    noWrap
                  >
                    {cellSubtitle.subtitle}
                  </Subtitle>
                  {cellSubtitle.isRemovedMember && (
                    <Subtitle
                      color="error.500"
                      noWrap
                      className={clsx(courseUserCellClasses.removedMember, classes?.removedMember)}
                    >
                      {t('removed_member', { ns: i18nNS.COMMON })}
                    </Subtitle>
                  )}
                </Stack>
              ) : (
                cellSubtitle.subtitle
              )}
            </>
          ) : null
        ) : (
          <Skeleton variant="text" width={200} />
        )}
      </Content>
      {cellTitle ? (
        <>
          {typeof action === 'string' ? (
            <Action
              className={clsx(courseUserCellClasses.action, classes?.action)}
              aria-hidden={hideAriaContent}
            >
              {action}
            </Action>
          ) : (
            action
          )}
        </>
      ) : null}
    </Root>
  );
};

export default CourseUserCell;
